import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { AssignedVisitUser } from "@/api/assigned-user-visits"

import {
  enableAssignedVisits,
  getAdminAssignedVisits,
  getLeadAssignedVisits,
} from "@/api/assigned-user-visits"
import { Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DashboardSectionCard } from "@/features/admin-dashboard/components/admin-dashboard-shared"
import { useAuthGeneration } from "@/hooks/use-auth-generation"

type Props = {
  mode: "admin" | "lead"
  enabled: boolean
  groupValue?: string
  canEnable?: boolean
}

export function AssignedUserVisitsCard({ mode, enabled, groupValue, canEnable = false }: Props) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [days, setDays] = useState(30)
  const [draftDays, setDraftDays] = useState("30")
  const [visible, setVisible] = useState(50)
  const authGeneration = useAuthGeneration()
  const query = useQuery({
    queryKey: ["assigned-user-visits", mode, groupValue, days, authGeneration],
    queryFn: () =>
      mode === "admin" ? getAdminAssignedVisits(days) : getLeadAssignedVisits(days, groupValue!),
    enabled: enabled && (mode === "admin" || !!groupValue),
    retry: false,
  })
  const enableMutation = useMutation({
    mutationFn: enableAssignedVisits,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-config"] })
    },
  })
  const parsedDays = Number(draftDays)
  const validDays = Number.isInteger(parsedDays) && parsedDays >= 1 && parsedDays <= 365
  const date = (value: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language, { dateStyle: "medium" }) : "—"
  const statusLabel = (status: AssignedVisitUser["status"]) => {
    if (status === "not_yet_seen")
      return t("localAiLeadPage.assignedVisits.notYetSeen", "Not yet seen")
    if (status === "inactive") return t("localAiLeadPage.assignedVisits.inactive", "Inactive")
    return t("localAiLeadPage.assignedVisits.unknown", "Earlier history unknown")
  }

  if (!enabled && !canEnable) return null

  return (
    <DashboardSectionCard
      icon={Users}
      id={mode === "admin" ? "admin-assigned-user-visits" : "lead-assigned-user-visits"}
      title={t("localAiLeadPage.assignedVisits.title", "Assigned users and visits")}
      description={t(
        "localAiLeadPage.assignedVisits.description",
        "Hello AI app visits observed since tracking began. Earlier history may be unknown."
      )}
    >
      {!enabled ? (
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            {t(
              "localAiLeadPage.assignedVisits.off",
              "Visit tracking is off for this tenant. Enable it to start collecting observations."
            )}
          </p>
          <Button
            disabled={enableMutation.isPending}
            type="button"
            onClick={() => enableMutation.mutate()}
          >
            {t("localAiLeadPage.assignedVisits.enable", "Enable visit tracking")}
          </Button>
          {enableMutation.isError ? (
            <p className="text-destructive text-sm">
              {t("localAiLeadPage.assignedVisits.enableError", "Could not enable visit tracking.")}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              if (validDays) {
                setDays(parsedDays)
                setVisible(50)
              }
            }}
          >
            <label className="text-sm" htmlFor={`${mode}-assigned-visits-days`}>
              {t("localAiLeadPage.assignedVisits.days", "No visit in the last X days")}
              <Input
                className="mt-1 w-28"
                id={`${mode}-assigned-visits-days`}
                max={365}
                min={1}
                type="number"
                value={draftDays}
                onChange={(event) => setDraftDays(event.target.value)}
              />
            </label>
            <Button disabled={!validDays} type="submit" variant="outline">
              {t("localAiLeadPage.assignedVisits.apply", "Apply")}
            </Button>
          </form>
          {query.isLoading ? (
            <p className="text-muted-foreground text-sm">
              {t("localAiLeadPage.assignedVisits.loading", "Loading assigned users…")}
            </p>
          ) : null}
          {query.isError ? (
            <p className="text-destructive text-sm" role="alert">
              {t(
                "localAiLeadPage.assignedVisits.error",
                "Could not load assignments or visits. Ask an administrator to check Microsoft Graph access and storage."
              )}
            </p>
          ) : null}
          {query.isSuccess ? (
            <>
              <p className="text-muted-foreground text-sm">
                {t("localAiLeadPage.assignedVisits.coverage", "Tracking since {{date}}", {
                  date: date(query.data.trackingStartedAt),
                })}
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                <span>
                  {t("localAiLeadPage.assignedVisits.total", "Assigned: {{count}}", {
                    count: query.data.totalAssigned,
                  })}
                </span>
                <span>
                  {t("localAiLeadPage.assignedVisits.notYetSeenCount", "Not yet seen: {{count}}", {
                    count: query.data.counts.not_yet_seen,
                  })}
                </span>
                <span>
                  {t("localAiLeadPage.assignedVisits.inactiveCount", "Inactive: {{count}}", {
                    count: query.data.counts.inactive,
                  })}
                </span>
                <span>
                  {t("localAiLeadPage.assignedVisits.unknownCount", "Unknown: {{count}}", {
                    count: query.data.counts.history_unknown,
                  })}
                </span>
              </div>
              {query.data.users.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t(
                    "localAiLeadPage.assignedVisits.empty",
                    "No assigned users need follow-up for this period."
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  {query.data.users.slice(0, visible).map((user) => (
                    <div
                      key={user.userId}
                      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{user.name}</p>
                        <p className="text-muted-foreground break-all">{user.email}</p>
                        {mode === "admin" && user.groupValue ? (
                          <p className="text-muted-foreground">{user.groupValue}</p>
                        ) : null}
                      </div>
                      <div className="text-muted-foreground text-right">
                        <p>{statusLabel(user.status)}</p>
                        {user.lastVisitAt ? (
                          <p>
                            {t(
                              "localAiLeadPage.assignedVisits.lastVisit",
                              "Last observed {{date}}",
                              { date: date(user.lastVisitAt) }
                            )}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {visible < query.data.users.length ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setVisible(visible + 50)}
                    >
                      {t("localAiLeadPage.assignedVisits.showMore", "Show more")}
                    </Button>
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </DashboardSectionCard>
  )
}
